<template>
  <div class="ppe">
    <loading-panel :loading="loading"></loading-panel>
    <base-alert-dialog
      :show="confirmDialog.show"
      :variant="confirmDialog.variant"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :confirm-text="confirmDialog.confirmText"
      :cancel-text="confirmDialog.cancelText"
      :confirm-variant="confirmDialog.confirmVariant"
      show-cancel
      @confirm="onConfirmDialogConfirm"
      @cancel="onConfirmDialogCancel"
    />

    <!-- Web font loaders for the font pickers -->
    <div v-for="(f, i) in orderedFonts" :key="'fontlink-' + i" style="display: none">
      <link rel="stylesheet" :href="'https://fonts.googleapis.com/css?family=' + encodeURIComponent(f.family)">
    </div>

    <div v-if="!loading">
      <breadcrumb>
        <breadcrumb-item>
          <a href="#" @click.prevent="$router.push('/bookings/request-forms')">Request forms</a>
        </breadcrumb-item>
        <breadcrumb-item>
          <span>{{ headerTitle }}</span>
        </breadcrumb-item>
      </breadcrumb>

      <div class="row no-gutters ppe__layout">
        <div class="col-lg-12 col-12">
          <card class="ppe__editor mb-0">
            <div slot="header" class="ppe__header">
              <div class="ppe__header-title">
                <h4 class="text-white mb-0 ppe__title">{{ headerTitle }}</h4>
                <div class="ppe__header-meta">
                  <badge v-if="!isNew && form.archived === 1" type="warning">Archived</badge>
                  <badge v-else-if="!isNew && form.is_active !== 1" type="default">Paused</badge>
                  <badge v-else-if="!isNew" type="success">Live</badge>
                  <small v-if="!isNew && form.updated_at" class="ppe__edited">
                    Edited {{ relative(form.updated_at) }}
                  </small>
                </div>
              </div>
              <div class="ppe__header-actions">
                <el-dropdown v-if="!isNew">
                  <base-button link type="primary" size="sm">
                    <i style="font-size: large" class="tim-icons el-icon-more"></i>
                  </base-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('duplicate')">
                        <i class="tim-icons icon-single-copy-04 mr-2"></i> Duplicate
                      </a>
                      <hr>
                      <a v-if="form.archived !== 1" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('archive')">
                        <i class="tim-icons icon-trash-simple mr-2"></i> Archive
                      </a>
                      <a v-else class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('restore')">
                        <i class="tim-icons icon-refresh-02 mr-2"></i> Restore
                      </a>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </div>

            <div class="ppe__split">
              <aside class="ppe__nav">
                <editor-section-nav
                  :sections="sections"
                  :active-section="activeSection"
                  @navigate="setSection"
                />
              </aside>

              <div class="ppe__content">
                <!-- Details -->
                <div v-if="activeSection === 'details'" :key="'tab-details'">
                  <collapsible-section title="Details" icon="tim-icons icon-settings" :collapsible="false">
                    <div class="ppe__row">
                      <div class="ppe__col">
                        <base-input
                          :error="errs.name"
                          required
                          label="Name"
                          v-model="form.name"
                          maxlength="100"
                          placeholder="e.g. Field trips, Corporate events"
                        />
                        <p class="ppe__hint">Internal name. Customers see the page title instead.</p>
                      </div>
                      <div class="ppe__col">
                        <base-input
                          :error="errs.slug"
                          required
                          label="Link slug"
                          :value="form.slug"
                          maxlength="100"
                          placeholder="field-trips"
                          @input="onSlugInput"
                        />
                        <p class="ppe__hint">Lowercase letters, numbers and dashes. This is the last part of the public link.</p>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col">
                        <el-switch active-color="#e14eca" v-model="form.is_active" :active-value="1" :inactive-value="0" />
                        <label class="text-white"> &nbsp; Accepting requests
                          <el-popover trigger="hover" placement="right">
                            <div>
                              <div class="popover-body">Off pauses the form. Its public page shows the not-found page until it is switched back on.</div>
                            </div>
                            <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                          </el-popover>
                        </label>
                      </div>
                    </div>
                    <info-hint v-if="!isNew" icon="icon-link-72" variant="primary">
                      <a class="text-white" target="_blank" :href="publicUrl">{{ publicUrl }}</a>
                      <base-button icon class="btn-link text-white" title="Copy link" @click="copyLink">
                        <i class="tim-icons icon-single-copy-04"></i>
                      </base-button>
                    </info-hint>
                    <info-hint icon="icon-email-85" variant="primary" class="mt-3">
                      Notifications for this form are handled by email automations. Create an automation on the trigger Request form submitted to alert your team and reply to the customer with your own template.
                      <div class="mt-2">
                        <base-button variant="outlined" tone="primary" size="sm" @click="$router.push('/mail/custom/landing?tab=1')">
                          <i class="tim-icons icon-send"></i>Open automations
                        </base-button>
                      </div>
                    </info-hint>
                  </collapsible-section>
                </div>

                <!-- Content -->
                <div v-if="activeSection === 'content'" :key="'tab-content'">
                  <collapsible-section title="Content" icon="tim-icons icon-align-left-2" :collapsible="false">
                    <base-input
                      label="Page title"
                      v-model="form.title"
                      maxlength="180"
                      placeholder="Request an event"
                    />
                    <base-input label="Introduction">
                      <el-input
                        v-model="form.intro_text"
                        type="textarea"
                        :rows="3"
                        :maxlength="1000"
                        show-word-limit
                        placeholder="Tell customers what to include in their request."
                      />
                    </base-input>
                    <base-input label="Confirmation message">
                      <el-input
                        v-model="form.confirmation_message"
                        type="textarea"
                        :rows="3"
                        :maxlength="1000"
                        show-word-limit
                        placeholder="Thanks. We will review your request and reply by email."
                      />
                    </base-input>
                  </collapsible-section>
                </div>

                <!-- Appearance -->
                <div v-if="activeSection === 'appearance'" :key="'tab-appearance'">
                  <collapsible-section
                    title="Logo"
                    icon="tim-icons icon-image-02"
                    :badge="form.show_logo === 1 ? 'Shown' : ''"
                  >
                    <toggle-field v-model="form.show_logo" label="Show logo on this page" :class="form.show_logo === 1 ? 'mb-3' : 'mb-0'" />
                    <template v-if="form.show_logo === 1">
                      <div v-if="!form.logo_image_url">
                        <div class="ppe__logo-drop" @click="openLogoEditor">
                          <i class="tim-icons icon-cloud-upload-94 ppe__logo-icon"></i>
                          <p class="ppe__logo-drop-title">Upload a logo</p>
                          <p class="ppe__hint mb-0">PNG, JPG, or SVG. Drag to position after upload.</p>
                        </div>
                        <image-editor
                          ref="logoEditor"
                          size="sm"
                          text="Upload"
                          @saveImage="onLogoSave"
                          class="ppe__logo-editor-hidden"
                        />
                      </div>
                      <div v-else class="ppe__logo-preview">
                        <img
                          :src="form.logo_image_url"
                          :style="logoPreviewStyle"
                          alt="Logo"
                        />
                        <div class="ppe__logo-actions">
                          <image-editor
                            :edited_image="form.logo_image"
                            :edit="true"
                            @saveImage="onLogoSave"
                          />
                          <base-button link type="primary" size="sm" @click="removeLogo()">Remove</base-button>
                        </div>
                      </div>
                    </template>
                  </collapsible-section>

                  <collapsible-section title="Typography" icon="tim-icons icon-pencil" :default-open="false">
                    <div class="ppe__row">
                      <div class="ppe__col">
                        <label class="text-white">Body font</label>
                        <base-input>
                          <el-select
                            v-model="form.font_family"
                            class="select-primary"
                            :style="{ 'font-family': form.font_family }"
                            placeholder="Select a font"
                            filterable
                          >
                            <el-option
                              v-for="f in orderedFonts"
                              :key="'body-' + f.family"
                              :style="{ 'font-family': f.family }"
                              class="select-primary"
                              :label="f.family"
                              :value="f.family"
                            />
                          </el-select>
                        </base-input>
                      </div>
                      <div class="ppe__col">
                        <label class="text-white">Card font</label>
                        <base-input>
                          <el-select
                            v-model="form.font_family_card"
                            class="select-primary"
                            :style="{ 'font-family': form.font_family_card }"
                            placeholder="Select a font"
                            filterable
                          >
                            <el-option
                              v-for="f in orderedFonts"
                              :key="'card-' + f.family"
                              :style="{ 'font-family': f.family }"
                              class="select-primary"
                              :label="f.family"
                              :value="f.family"
                            />
                          </el-select>
                        </base-input>
                      </div>
                    </div>
                  </collapsible-section>

                  <collapsible-section title="Color palette" icon="tim-icons icon-palette" :default-open="false">
                    <div class="ppe__palette">
                      <section class="ppe__palette-group">
                        <header class="ppe__palette-group-head">
                          <span class="ppe__palette-group-eyebrow">Page</span>
                        </header>
                        <div class="ppe__palette-group-fields">
                          <color-field v-model="form.background_color" label="Background" />
                          <color-field v-model="form.text_color" label="Text" />
                          <color-field v-model="form.link_color" label="Links" />
                        </div>
                      </section>

                      <section class="ppe__palette-group">
                        <header class="ppe__palette-group-head">
                          <span class="ppe__palette-group-eyebrow">Components</span>
                        </header>
                        <div class="ppe__palette-group-fields">
                          <color-field v-model="form.card_background_color" label="Card background" />
                          <color-field v-model="form.card_text_color" label="Card text" />
                          <color-field v-model="form.button_color" label="Button fill" />
                          <color-field v-model="form.button_text_color" label="Button label" />
                          <color-field v-model="form.input_border_color" label="Input border" />
                        </div>
                      </section>
                    </div>
                    <div class="ppe__palette-footer">
                      <a href="#" class="ppe__palette-reset" @click.prevent="resetColors">
                        <i class="tim-icons icon-refresh-01"></i>
                        Reset to defaults
                      </a>
                    </div>
                  </collapsible-section>
                </div>

                <!-- Events -->
                <div v-if="activeSection === 'events'" :key="'tab-events'">
                  <collapsible-section
                    title="Events"
                    icon="tim-icons icon-puzzle-10"
                    :collapsible="false"
                    :badge="form.show_event_picker === 1 && linkedGames.length ? String(linkedGames.length) : ''"
                  >
                    <div class="row">
                      <div class="col">
                        <el-switch active-color="#e14eca" v-model="form.show_event_picker" :active-value="1" :inactive-value="0" />
                        <label class="text-white"> &nbsp; Let customers choose an event
                          <el-popover trigger="hover" placement="right">
                            <div>
                              <div class="popover-body">Adds an event picker to the form. Off hides it and the request comes in without an event.</div>
                            </div>
                            <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                          </el-popover>
                        </label>
                      </div>
                    </div>
                    <template v-if="form.show_event_picker === 1">
                      <div class="row mt-2">
                        <div class="col-lg-8">
                          <base-input label="Events on this form">
                            <el-select
                              v-model="linkedGames"
                              class="select-primary"
                              multiple
                              filterable
                              placeholder="Every active event"
                            >
                              <el-option
                                v-for="g in availableGames"
                                :key="'game-' + g.id"
                                class="select-primary"
                                :label="g.internal_name || g.name"
                                :value="g.id"
                              />
                            </el-select>
                          </base-input>
                        </div>
                      </div>
                      <info-hint variant="neutral">Leave empty to offer every active event.</info-hint>
                    </template>
                  </collapsible-section>
                </div>

                <!-- Form: what the customer sees, top to bottom -->
                <div v-if="activeSection === 'form'" :key="'tab-form'">
                  <collapsible-section
                    title="Form"
                    icon="tim-icons icon-notes"
                    :collapsible="false"
                    :badge="linkedCustomFields.length ? String(linkedCustomFields.length) : ''"
                  >
                    <custom-field-link-cards
                      :items="linkedCustomFields"
                      :addable="addableCustomFields"
                      :toggles="customFieldToggles"
                      :locked-items="lockedRows"
                      add-placeholder="+ Add question"
                      remove-tooltip="Remove from this form"
                      empty-title="No questions yet"
                      empty-linked-hint="Add questions like group size, budget or occasion to ask on this form."
                      @add="addCustomField"
                      @remove="removeCustomField"
                      @toggle="setCustomFieldFlag"
                      @locked-toggle="setFormSection"
                    />
                  </collapsible-section>
                </div>

              </div>
            </div>

            <!-- Footer: save -->
            <div class="ppe__footer">
              <span class="ppe__footer-msg" :class="{ 'ppe__footer-msg--dirty': isDirty }">
                <span v-if="isDirty" class="ppe__footer-dot"></span>
                <span v-if="missingRequired">{{ missingRequired }}</span>
                <span v-else-if="isDirty">Unsaved changes</span>
                <span v-else-if="isNew">Ready to create</span>
                <span v-else>All changes saved</span>
              </span>
              <div class="ppe__footer-actions">
                <base-button
                  v-if="isNew"
                  simple
                  type="primary"
                  size="sm"
                  @click="cancel"
                >
                  Cancel
                </base-button>
                <base-button
                  type="primary"
                  size="sm"
                  @click="save"
                  :disabled="!canSave"
                >
                  <span v-if="!saving">{{ isNew ? 'Create form' : 'Save changes' }}</span>
                  <span v-else>Saving...</span>
                </base-button>
              </div>
            </div>
          </card>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { Select, Option, Dropdown, DropdownMenu, Switch } from "element-ui";
import { Badge } from "src/components";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";
import EditorSectionNav from "@/components/PaymentPages/EditorSectionNav";
import ColorField from "@/components/PaymentPages/ColorField";
import ToggleField from "@/components/PaymentPages/ToggleField";
import CollapsibleSection from "@/pages/Bookings/BookinSiteSettings/components/shared/CollapsibleSection";
import ImageEditor from "@/components/ImageEditor";
import axios from "axios";
import _ from "lodash";
import { Auth } from "aws-amplify";
import { API_LOCATION, REQUEST_URL } from "@/api-config";

// Strip the system-font fallback stack off a stored font-family value so the
// editor dropdown matches an <el-option> value.
function primaryFamilyOf(stack) {
  if (!stack) return 'Poppins';
  const first = String(stack).split(',')[0]?.trim().replace(/^["']|["']$/g, '');
  return first || 'Poppins';
}

// Lowercase, non [a-z0-9] runs to a dash, trimmed, capped at the column width.
function slugify(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
}

const SLUG_RE = /^[a-z0-9-]{1,100}$/;

const COLOR_DEFAULTS = {
  background_color: '#27293d', text_color: '#ffffff', link_color: '#e14eca',
  card_background_color: '#1e1e2f', card_text_color: '#ffffff',
  button_color: '#e14eca', button_text_color: '#ffffff',
  input_border_color: '#7d7d7d99',
};

// Same values as the request_forms column defaults.
const DEFAULT_FORM = {
  name: '', slug: '', title: '', intro_text: '', confirmation_message: '',
  show_event_picker: 1, show_preferred_date: 1, show_preferred_slots: 1, show_party_size: 1,
  is_active: 1, archived: 0,
  logo_image_id: null, logo_image_url: null, logo_image: null, show_logo: 1,
  ...COLOR_DEFAULTS,
  font_family: 'Poppins',
  font_family_card: 'Poppins',
  created_at: null, updated_at: null,
};

const ALLOWED_COLUMNS = [
  'name', 'slug', 'title', 'intro_text', 'confirmation_message',
  'show_event_picker', 'show_preferred_date', 'show_preferred_slots', 'show_party_size',
  'logo_image_id', 'show_logo',
  'background_color', 'text_color', 'link_color', 'card_background_color', 'card_text_color',
  'button_color', 'button_text_color', 'font_family', 'font_family_card', 'input_border_color',
  'is_active', 'archived',
];
const BOOLEAN_COLUMNS = new Set([
  'show_event_picker', 'show_preferred_date', 'show_preferred_slots', 'show_party_size',
  'show_logo', 'is_active', 'archived',
]);

export default {
  name: 'RequestFormEditor',
  components: {
    Breadcrumb, BreadcrumbItem,
    EditorSectionNav, ColorField, ToggleField,
    CollapsibleSection,
    ImageEditor,
    Badge,
    [Switch.name]: Switch,
    [Select.name]: Select,
    [Option.name]: Option,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
  },
  data() {
    return {
      confirmDialog: { show: false, title: '', message: '', confirmText: 'Confirm', cancelText: 'Cancel', confirmVariant: 'primary', variant: 'warning', resolve: null },
      loading: false,
      saving: false,
      activeSection: 'details',
      form: JSON.parse(JSON.stringify(DEFAULT_FORM)),
      // Ordered game ids offered on the form; empty means every active event.
      linkedGames: [],
      linkedCustomFields: [],
      availableGames: [],
      availableCustomFields: [],
      fonts: [],
      // While a form is new the slug follows the name until the user edits it.
      slugEdited: false,
      errs: { name: '', slug: '' },
      pristine: '',
    };
  },
  computed: {
    formId() {
      const id = this.$route.params.id;
      return !id || id === 'new' ? null : Number(id);
    },
    isNew() { return this.formId === null; },
    headerTitle() {
      if (this.isNew) return 'New request form';
      return this.form.name || 'Untitled form';
    },
    groupCode() {
      const group = JSON.parse(localStorage.getItem('group') || '{}');
      return group.code || '';
    },
    publicUrl() {
      return REQUEST_URL + this.groupCode + '/' + (this.form.slug || '');
    },
    sections() {
      return [
        { id: 'details',       label: 'Details', warn: !this.form.name || !this.form.slug },
        { id: 'content',       label: 'Content' },
        { id: 'appearance',    label: 'Appearance' },
        { id: 'events',        label: 'Events' },
        { id: 'form',          label: 'Form' },
      ];
    },
    orderedFonts() {
      const skip = new Set([
        'Libre Baskerville',
        'Material Icons',
        'Material Icons Outlined',
        'Material Icons Sharp',
        'Material Icons Round',
        'M PLUS Rounded 1c',
      ]);
      const filtered = (this.fonts || []).filter(f => !skip.has(f.family));
      const deduped = _.uniqBy(filtered, 'family');
      return _.orderBy(deduped, 'family');
    },
    logoPreviewStyle() {
      const img = this.form.logo_image;
      if (!img) return {};
      return {
        transform: `translate(0%, 0%) scale(${(img.scale || 100) * 0.01})`,
        left: (img.x || 0) + 'px',
        top: (img.y || 0) + 'px',
        objectFit: img.object_fit || 'cover',
      };
    },
    isDirty() {
      if (!this.pristine) return false;
      return this.currentSnapshot() !== this.pristine;
    },
    missingRequired() {
      const hasName = !!(this.form.name && this.form.name.trim());
      const hasSlug = !!(this.form.slug && this.form.slug.trim());
      if (hasName && hasSlug) return '';
      if (!hasName && !hasSlug) return 'Enter a name and link slug to continue';
      return hasName ? 'Enter a link slug to continue' : 'Enter a name to continue';
    },
    canSave() {
      if (this.saving) return false;
      if (this.missingRequired) return false;
      return this.isDirty || this.isNew;
    },
    // Selected events in form order, as the preview's event picker lists them.
    lockedRows() {
      const f = this.form;
      const rows = [
        { key: 'first_name', name: 'First name', typeName: 'INPUT FIELD', icon: 'tim-icons icon-pencil', status: 'required' },
        { key: 'last_name', name: 'Last name', typeName: 'INPUT FIELD', icon: 'tim-icons icon-pencil', status: 'required' },
        { key: 'email', name: 'Email', typeName: 'INPUT FIELD', icon: 'tim-icons icon-email-85', status: 'required' },
        { key: 'phone', name: 'Phone', typeName: 'INPUT FIELD', icon: 'tim-icons icon-mobile', status: 'optional' },
        { key: 'show_event_picker', name: 'Event', typeName: 'DROPDOWN', icon: 'tim-icons icon-puzzle-10', hint: 'Events are chosen in the Events section', toggle: true, value: f.show_event_picker },
        { key: 'show_preferred_date', name: 'Preferred date and time', typeName: 'DATE AND TIME', icon: 'tim-icons icon-calendar-60', toggle: true, value: f.show_preferred_date },
      ];
      if (f.show_preferred_date === 1) {
        rows.push({ key: 'show_preferred_slots', name: 'Preferred time slots', typeName: 'UP TO THREE OPEN SLOTS', icon: 'tim-icons icon-time-alarm', hint: 'A preference, nothing is held', toggle: true, value: f.show_preferred_slots, nested: true });
      }
      rows.push({ key: 'show_party_size', name: 'Number of people', typeName: 'NUMBER', icon: 'tim-icons icon-single-02', hint: 'Prefills the players when staff book', toggle: true, value: f.show_party_size });
      rows.push({ key: 'message', name: 'Tell us about your event', typeName: 'LONG TEXT', icon: 'tim-icons icon-align-left-2', status: 'required' });
      return rows;
    },
    addableCustomFields() {
      const linked = new Set(this.linkedCustomFields.map(f => f.custom_field_id));
      return this.availableCustomFields.filter(f => !linked.has(f.id));
    },
    customFieldToggles() {
      // Mirrors BSS ContentTab: "required" applies to every type (on a
      // checkbox it means the box must be checked); "requires user
      // interaction" is checkbox-only; alert applies to every type.
      return [
        {
          key: 'required',
          label: 'Required',
          tooltip: 'Make field required',
        },
        {
          key: 'interaction_required',
          label: 'Requires user interaction',
          appliesTo: (f) => Number(f.type) === 1,
        },
        {
          key: 'add_alert',
          label: 'Creates alert',
          tooltip: 'Show alert on the transaction when filled out',
        },
      ];
    },
  },
  watch: {
    'form.name'(name) {
      if (this.isNew && !this.slugEdited) this.form.slug = slugify(name);
    },
  },
  methods: {
    setFormSection(key, value) {
      if (Object.prototype.hasOwnProperty.call(this.form, key)) this.$set(this.form, key, value);
    },
    async getAuthToken() {
      const data = await Auth.currentSession();
      return data.idToken.jwtToken;
    },
    apiBaseUrl() {
      const companyId = localStorage.getItem('companyId');
      const companyGroupId = JSON.parse(localStorage.getItem('group')).id;
      return `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/request-forms`;
    },
    // Error bodies are JSON ({ error, validation_errors? }); fall back to a
    // plain string body, then to the caller's message.
    errorMessage(err, fallback) {
      const data = err && err.response ? err.response.data : null;
      if (data && typeof data === 'object') {
        const ve = data.validation_errors;
        if (ve && typeof ve === 'object') {
          const first = Object.values(ve).find(v => typeof v === 'string');
          if (first) return first;
        }
        if (data.error) return data.error;
      }
      if (typeof data === 'string' && data) {
        try {
          const parsed = JSON.parse(data);
          if (parsed && parsed.error) return parsed.error;
        } catch (e) {
          return data;
        }
      }
      return fallback;
    },
    onSlugInput(value) {
      this.slugEdited = true;
      this.form.slug = String(value || '').toLowerCase();
    },
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.publicUrl);
        this.$notify({ type: 'success', message: 'Link copied', icon: 'tim-icons icon-check-2', timeout: 2000 });
      } catch (err) {
        console.error(err);
        this.$notify({ type: 'danger', message: 'Could not copy the link', icon: 'tim-icons icon-alert-circle-exc' });
      }
    },
    hydrateLinks(linked_games, linked_custom_fields, available_games, available_fields) {
      this.linkedGames = _.orderBy(linked_games || [], 'position').map((g) => g.game_id);
      this.linkedCustomFields = (linked_custom_fields || []).map((f) => ({
        id: f.id,
        custom_field_id: f.custom_field_id,
        name: f.label, type: f.type,
        position: f.position, active: f.active,
        required: f.required,
        interaction_required: f.interaction_required,
        add_alert: f.add_alert,
      }));
      this.availableGames = available_games || [];
      this.availableCustomFields = (available_fields || []).map((f) => ({
        id: f.id, name: f.label, type: f.type,
        // Library defaults seed the link row's flags at add time.
        required: f.required,
        interaction_required: f.interaction_required,
        add_alert: f.add_alert,
      }));
    },
    async fetch() {
      this.loading = true;
      try {
        const token = await this.getAuthToken();
        const url = `${this.apiBaseUrl()}/${this.isNew ? 'new' : this.formId}`;
        const res = await axios.get(url, { headers: { Authorization: token } });
        const { request_form, linked_games, linked_custom_fields, available_games, available_fields } = res.data;
        this.hydrateLinks(linked_games, linked_custom_fields, available_games, available_fields);
        if (this.isNew || !request_form) {
          this.form = JSON.parse(JSON.stringify(DEFAULT_FORM));
          this.slugEdited = false;
        } else {
          this.form = { ...JSON.parse(JSON.stringify(DEFAULT_FORM)), ...request_form };
          // Normalize legacy "Poppins, system-ui, ..." stacks to the family alone.
          this.form.font_family = primaryFamilyOf(this.form.font_family);
          this.form.font_family_card = primaryFamilyOf(this.form.font_family_card);
          BOOLEAN_COLUMNS.forEach((k) => { this.form[k] = Number(this.form[k]) === 1 ? 1 : 0; });
          this.form.title = this.form.title || '';
          this.form.intro_text = this.form.intro_text || '';
          this.form.confirmation_message = this.form.confirmation_message || '';
          // The image editor re-edits from an image object; rebuild it from the
          // flat logo_image_* columns the lambda joins in.
          this.form.logo_image = this.form.logo_image_id && this.form.logo_image_url
            ? {
              id: this.form.logo_image_id,
              url: this.form.logo_image_url,
              scale: this.form.logo_image_scale,
              background_color: this.form.logo_image_background_color,
              description: '',
              x: this.form.logo_image_x,
              y: this.form.logo_image_y,
              object_fit: this.form.logo_image_object_fit,
            }
            : null;
          this.slugEdited = true;
        }
        this.snapshotPristine();
      } catch (err) {
        console.error('Failed to load request form', err);
        if (err.response?.status === 404) {
          this.$notify({ type: 'danger', message: 'Request form not found' });
          this.$router.push('/bookings/request-forms');
        } else {
          this.$notify({ type: 'danger', message: this.errorMessage(err, 'Could not load request form') });
        }
      } finally {
        this.loading = false;
      }
    },
    currentSnapshot() {
      // Everything buildSavePayload() persists must be represented here, or
      // edits to it never light up Save.
      return JSON.stringify({
        form: this.form,
        games: this.linkedGames,
        cf: this.linkedCustomFields,
      });
    },
    snapshotPristine() { this.pristine = this.currentSnapshot(); },
    setSection(id) { this.activeSection = id; },
    addCustomField(id) {
      const cf = this.availableCustomFields.find(f => f.id === id); if (!cf) return;
      this.linkedCustomFields.push({
        id: `tmp-${Date.now()}`,
        custom_field_id: cf.id, name: cf.name, type: cf.type,
        position: this.linkedCustomFields.length, active: 1,
        required: cf.required ? 1 : 0,
        interaction_required: cf.interaction_required ? 1 : 0,
        add_alert: cf.add_alert ? 1 : 0,
      });
    },
    setCustomFieldFlag(idx, key, value) {
      this.$set(this.linkedCustomFields[idx], key, value);
    },
    removeCustomField(idx) { this.linkedCustomFields.splice(idx, 1); },
    resetColors() {
      Object.keys(COLOR_DEFAULTS).forEach((k) => { this.form[k] = COLOR_DEFAULTS[k]; });
    },
    openLogoEditor() {
      if (this.$refs.logoEditor) {
        this.$refs.logoEditor.uploadNew();
      }
    },
    async onLogoSave(image) {
      const token = await this.getAuthToken();
      const companyId = localStorage.getItem('companyId');
      const companyGroupId = JSON.parse(localStorage.getItem('group')).id;
      const imagesUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/images`;
      const body = {
        url: image.url,
        scale: image.scale,
        description: image.description,
        background_color: image.background_color,
        x: image.x,
        y: image.y,
        is_primary: 0,
        object_fit: image.object_fit,
      };
      try {
        let imageId;
        if (image.image_id) {
          await axios.put(`${imagesUrl}/${image.image_id}`, body, {
            headers: { Authorization: token },
          });
          imageId = image.image_id;
        } else {
          const res = await axios.post(imagesUrl, body, {
            headers: { Authorization: token },
          });
          imageId = Number(res.data);
        }
        this.form.logo_image_id = imageId;
        this.form.logo_image_url = image.url;
        this.form.logo_image = {
          id: imageId,
          url: image.url,
          scale: image.scale,
          background_color: image.background_color,
          description: image.description,
          x: image.x,
          y: image.y,
          object_fit: image.object_fit,
        };
      } catch (err) {
        console.error('Logo save failed', err);
        this.$notify({ type: 'danger', message: 'Could not save logo' });
      }
    },
    removeLogo() {
      this.form.logo_image_id = null;
      this.form.logo_image_url = null;
      this.form.logo_image = null;
    },
    buildSavePayload() {
      // Only the request_forms columns the lambdas allow-list; the derived
      // logo_image_* fields, ids and timestamps never go on the wire.
      const payload = {};
      ALLOWED_COLUMNS.forEach((k) => {
        const v = this.form[k];
        if (BOOLEAN_COLUMNS.has(k)) {
          payload[k] = Number(v) === 1 ? 1 : 0;
        } else if (k === 'logo_image_id') {
          payload[k] = v == null ? null : Number(v);
        } else {
          payload[k] = typeof v === 'string' ? v.trim() : v;
        }
      });
      payload.linked_games = this.linkedGames.map((game_id, i) => ({ game_id, position: i }));
      payload.linked_custom_fields = this.linkedCustomFields.map((f, i) => ({
        custom_field_id: f.custom_field_id,
        position: i,
        active: f.active ? 1 : 0,
        required: f.required ? 1 : 0,
        interaction_required: f.interaction_required ? 1 : 0,
        add_alert: f.add_alert ? 1 : 0,
      }));
      return payload;
    },
    validate() {
      this.errs.name = this.form.name && this.form.name.trim() ? '' : 'Name is required';
      const slug = String(this.form.slug || '').trim();
      this.errs.slug = SLUG_RE.test(slug) ? '' : 'Use lowercase letters, numbers and dashes';
      const first = this.errs.name || this.errs.slug;
      if (first) {
        this.$notify({ type: 'danger', message: first });
        this.activeSection = 'details';
        return false;
      }
      return true;
    },
    async save() {
      if (!this.validate()) return;
      this.saving = true;
      try {
        const token = await this.getAuthToken();
        const payload = this.buildSavePayload();
        if (this.isNew) {
          const res = await axios.post(this.apiBaseUrl(), payload, {
            headers: { Authorization: token },
          });
          const newId = res.data.id;
          this.$notify({ type: 'success', message: 'Form created' });
          // Redirect to the canonical /:id URL, then re-fetch: the replace
          // reuses this component, so mounted() never re-runs and the pristine
          // snapshot would stay pre-save without it.
          try {
            await this.$router.replace(`/bookings/request-forms/${newId}`);
            await this.fetch();
          } catch (postCreateErr) {
            console.error('Post-create refresh failed', postCreateErr);
          }
          if (!this.form.id) this.snapshotPristine();
        } else {
          await axios.put(`${this.apiBaseUrl()}/${this.formId}`, payload, {
            headers: { Authorization: token },
          });
          this.snapshotPristine();
          this.$notify({ type: 'success', message: 'Saved' });
        }
      } catch (err) {
        console.error('Save failed', err);
        this.$notify({ type: 'danger', message: this.errorMessage(err, 'Could not save request form') });
      } finally {
        this.saving = false;
      }
    },
    cancel() { this.$router.push({ path: '/bookings/request-forms' }); },
    // Shared confirm dialog (base-alert-dialog). Resolves true on confirm.
    askConfirm({ title, message, confirmText = 'Confirm', cancelText = 'Cancel', confirmVariant = 'primary', variant = 'warning' }) {
      return new Promise((resolve) => {
        this.confirmDialog = { show: true, title, message, confirmText, cancelText, confirmVariant, variant, resolve };
      });
    },
    onConfirmDialogConfirm() {
      const resolve = this.confirmDialog.resolve;
      this.confirmDialog.show = false;
      if (resolve) resolve(true);
    },
    onConfirmDialogCancel() {
      const resolve = this.confirmDialog.resolve;
      this.confirmDialog.show = false;
      if (resolve) resolve(false);
    },
    async handleAction(command) {
      if (this.isNew) return;
      const prompts = {
        duplicate: { title: 'Duplicate this form?', message: 'A paused copy is created with its own link. You will be taken to the copy.', confirmText: 'Duplicate' },
        archive: { title: 'Archive this form?', message: 'Its public page will show the not-found page and it will no longer accept requests.', confirmText: 'Archive', confirmVariant: 'danger' },
        restore: { title: 'Restore this form?', message: 'The form accepts requests again as soon as it is set to accepting requests.', confirmText: 'Restore' },
      };
      const prompt = prompts[command];
      if (!prompt) return;
      const confirmed = await this.askConfirm(prompt);
      if (!confirmed) return;
      const token = await this.getAuthToken();
      const base = `${this.apiBaseUrl()}/${this.formId}`;
      // Archive and restore only move the archived flag; keep any other
      // unsaved edits flagged as unsaved.
      const wasDirty = this.isDirty;
      try {
        switch (command) {
          case 'duplicate': {
            const res = await axios.post(`${base}/duplicate`, {}, {
              headers: { Authorization: token },
            });
            this.$notify({ type: 'success', message: 'Duplicated' });
            this.$router.push(`/bookings/request-forms/${res.data.id}`);
            break;
          }
          case 'archive': {
            await axios.put(base, { archived: 1 }, {
              headers: { Authorization: token },
            });
            this.form.archived = 1;
            if (!wasDirty) this.snapshotPristine();
            this.$notify({ type: 'success', message: 'Archived' });
            break;
          }
          case 'restore': {
            await axios.put(base, { archived: 0 }, {
              headers: { Authorization: token },
            });
            this.form.archived = 0;
            if (!wasDirty) this.snapshotPristine();
            this.$notify({ type: 'success', message: 'Restored' });
            break;
          }
        }
      } catch (err) {
        console.error(`${command} failed`, err);
        this.$notify({ type: 'danger', message: this.errorMessage(err, `Could not ${command}`) });
      }
    },
    relative(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      const diff = Date.now() - d.getTime();
      const m = Math.floor(diff / 60000);
      const h = Math.floor(diff / 3600000);
      const day = Math.floor(diff / 86400000);
      if (m < 1) return 'just now';
      if (m < 60) return `${m}m ago`;
      if (h < 24) return `${h}h ago`;
      if (day < 7) return `${day}d ago`;
      if (day < 30) return `${Math.floor(day / 7)}w ago`;
      return d.toLocaleDateString();
    },
    async getFonts() {
      try {
        const res = await axios.get(API_LOCATION + 'fonts');
        this.fonts = res.data || [];
      } catch (e) {
        console.error('Failed to load fonts', e);
      }
    },
  },
  mounted() {
    this.fetch();
    this.getFonts();
  },
  async beforeRouteLeave(to, from, next) {
    if (!this.isDirty) {
      return next();
    }
    const confirmed = await this.askConfirm({
      title: 'Unsaved changes',
      message: 'You have unsaved changes on this form. Leave without saving?',
      confirmText: 'Leave',
      cancelText: 'Stay',
      confirmVariant: 'danger',
    });
    if (confirmed) {
      next();
    } else {
      next(false);
    }
  },
};
</script>

<style lang="scss" scoped>
</style>
